<template>
    <div class="roommate-profile-section">
        <v-row>
            <v-col class="pb-0">
                <h3>Roommate Profile</h3>
            </v-col>
        </v-row>

        <!-- Disabled State -->
        <v-row v-if="!user.needs_roommate">
            <v-col>
                <v-alert
                    type="info"
                    dense
                    text
                    class="mb-0"
                >
                    <small>Roommate search is turned off. Enable "Need a roommate?" above to complete this section.</small>
                </v-alert>
            </v-col>
        </v-row>

        <template v-else>
            <!-- Profile Photo -->
            <v-row>
                <v-col cols="12">
                    <v-row>
                        <v-col class="pb-0">
                            <h5>
                                Profile Photo
                                <span v-if="!user.image_url && !user.image_path" class="incomplete-badge">Required</span>
                            </h5>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pt-0">
                            <p class="extra-info">A profile photo helps potential roommates get to know you better.</p>
                        </v-col>
                    </v-row>
                    <v-row align="center">
                        <v-col cols="auto">
                            <v-avatar size="80" color="#F4F5F6">
                                <v-img :src="user.image_url">
                                    <template v-slot:placeholder>
                                        <v-icon color="grey lighten-1" size="40">mdi-account-outline</v-icon>
                                    </template>
                                </v-img>
                            </v-avatar>
                        </v-col>
                        <v-col>
                            <input type="file" ref="file" style="display: none" @change="previewImage" accept="image/*" />
                            <v-btn
                                outlined
                                color="primary"
                                @click="uploadPhoto"
                                class="btn-edurent font-weight-regular"
                                :loading="isUploadingPhoto"
                            >
                                <v-icon left small>mdi-camera</v-icon>
                                {{ user.image_url ? 'Change Photo' : 'Upload Photo' }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <!-- Hobbies -->
            <v-row>
                <v-col cols="12" class="pb-0">
                    <v-row>
                        <v-col class="pb-0">
                            <h5>
                                Top 3 Hobbies/Interests
                                <span v-if="!user.hobbies" class="incomplete-badge">Required</span>
                            </h5>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pt-0">
                            <p class="extra-info">Selecting hobbies helps us connect you with better matches</p>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pb-0">
                            <v-select
                                multiple
                                chips
                                deletable-chips
                                solo
                                flat
                                outlined
                                hide-details="auto"
                                :value="computedHobbies"
                                @input="onHobbiesInput"
                                :items="hobbiesItems"
                                placeholder="Select up to 3 hobbies"
                            >
                                <template slot="append">
                                    <span>
                                        <v-icon size="18">mdi-chevron-down</v-icon>
                                    </span>
                                </template>
                                <template v-slot:selection="{ item }">
                                    <v-chip close class="hobbies-chip" color="rgba(45, 118, 227, 0.15)">
                                        <span>{{ item }}</span>
                                    </v-chip>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <!-- Social Accounts -->
            <v-row class="d-flex mt-4">
                <v-col class="pb-0">
                    <h5>
                        Social Accounts
                        <span v-if="!hasSocialMedia" class="incomplete-badge">At least 1 required</span>
                    </h5>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="pt-0">
                    <p class="extra-info">Adding links to your social media accounts helps others get a better idea of your lifestyle</p>
                </v-col>
            </v-row>
            <v-row class="d-flex px-1">
                <v-col cols="12" md="6">
                    <v-text-field
                        outlined
                        placeholder="Enter Username"
                        class="btn-edurent font-weight-regular px-0"
                        hide-details="auto"
                        :value="user.instagram_username"
                        @input="$emit('update:user', { ...user, instagram_username: $event })"
                    >
                        <template slot="prepend-inner">
                            <span class="mr-2">
                                <v-img src="@/assets/icons/social-instagram-sm.png"></v-img>
                            </span>
                        </template>
                    </v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        outlined
                        placeholder="Enter Username"
                        class="btn-edurent font-weight-regular px-0"
                        hide-details="auto"
                        :value="user.tiktok_username"
                        @input="$emit('update:user', { ...user, tiktok_username: $event })"
                    >
                        <template slot="prepend-inner">
                            <span class="mr-2">
                                <v-img src="@/assets/icons/social-tiktok-sm.png"></v-img>
                            </span>
                        </template>
                    </v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        outlined
                        placeholder="Enter Username"
                        class="btn-edurent font-weight-regular px-0"
                        hide-details="auto"
                        :value="user.snapchat_username"
                        @input="$emit('update:user', { ...user, snapchat_username: $event })"
                    >
                        <template slot="prepend-inner">
                            <span class="mr-2">
                                <v-img src="@/assets/icons/social-snapchat-sm.png"></v-img>
                            </span>
                        </template>
                    </v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        outlined
                        placeholder="Enter Username"
                        class="btn-edurent font-weight-regular px-0"
                        hide-details="auto"
                        :value="user.facebook_username"
                        @input="$emit('update:user', { ...user, facebook_username: $event })"
                    >
                        <template slot="prepend-inner">
                            <span class="mr-2">
                                <v-img src="@/assets/icons/social-facebook-sm.png"></v-img>
                            </span>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <!-- Ideal Roommate Preferences -->
            <v-row>
                <v-col class="pb-0">
                    <h4>My Ideal Roommate</h4>
                </v-col>
            </v-row>

            <v-row>
                <!-- Gender Preference -->
                <v-col cols="12" md="6">
                    <v-row>
                        <v-col class="pb-0">
                            <h5>
                                Gender Preference
                                <span v-if="!user.ideal_roommate_gender" class="incomplete-badge">Required</span>
                            </h5>
                        </v-col>
                    </v-row>
                    <v-row class="d-flex px-2">
                        <v-col class="pb-0 px-1">
                            <v-btn
                                outlined
                                block
                                class="btn-edurent font-weight-regular"
                                height="45"
                                :class="user.ideal_roommate_gender === 'male' ? 'field-selected' : ''"
                                :color="user.ideal_roommate_gender === 'male' ? 'primary' : 'rgba(0,0,0,0.2)'"
                                @click="$emit('update:user', { ...user, ideal_roommate_gender: 'male' })"
                            >Male</v-btn>
                        </v-col>
                        <v-col class="pb-0 px-1">
                            <v-btn
                                outlined
                                block
                                class="btn-edurent font-weight-regular"
                                height="45"
                                :class="user.ideal_roommate_gender === 'female' ? 'field-selected' : ''"
                                :color="user.ideal_roommate_gender === 'female' ? 'primary' : 'rgba(0,0,0,0.2)'"
                                @click="$emit('update:user', { ...user, ideal_roommate_gender: 'female' })"
                            >Female</v-btn>
                        </v-col>
                        <v-col class="pb-0 px-1">
                            <v-btn
                                outlined
                                block
                                class="btn-edurent font-weight-regular"
                                height="45"
                                :class="user.ideal_roommate_gender === 'any' ? 'field-selected' : ''"
                                :color="user.ideal_roommate_gender === 'any' ? 'primary' : 'rgba(0,0,0,0.2)'"
                                @click="$emit('update:user', { ...user, ideal_roommate_gender: 'any' })"
                            >Any</v-btn>
                        </v-col>
                    </v-row>
                </v-col>

                <!-- Age Range -->
                <v-col cols="12" md="6">
                    <v-row>
                        <v-col class="pb-0">
                            <h5>
                                Age Range
                                <span v-if="!user.ideal_roommate_age_young || !user.ideal_roommate_age_old" class="incomplete-badge">Required</span>
                            </h5>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pb-0">
                            <v-card
                                class="pa-0 px-2 d-flex align-center"
                                outlined
                                height="45"
                            >
                                <v-range-slider
                                    v-model="ageRange"
                                    max="99"
                                    min="17"
                                    hide-details
                                    class="align-center"
                                    track-color="#d8d8d8"
                                    @change="onAgeRangeChange"
                                >
                                    <template v-slot:prepend>
                                        <div style="width: 30px" class="d-flex justify-center">
                                            {{ ageRange[0] }}
                                        </div>
                                    </template>
                                    <template v-slot:append>
                                        <div style="width: 30px" class="d-flex justify-center">
                                            {{ ageRange[1] }}
                                        </div>
                                    </template>
                                </v-range-slider>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <!-- SMS Preference -->
            <v-row>
                <v-col cols="12" class="py-0">
                    <v-checkbox
                        label="Allow text messages from my matches"
                        class="py-0 my-0 preference-check"
                        :input-value="user.allow_roommate_sms"
                        @change="$emit('update:user', { ...user, allow_roommate_sms: $event })"
                    ></v-checkbox>
                </v-col>
            </v-row>
        </template>
    </div>
</template>

<script>
import UserService from '@/services/user.service'

export default {
    name: 'RoommateProfileSection',
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isUploadingPhoto: false,
            ageRange: [17, 99],
            hobbiesItems: ['Music', 'Sports', 'Gaming', 'Parties', 'Pets', 'Reading', 'TV Shows']
        }
    },
    computed: {
        computedHobbies() {
            if (!this.user || !this.user.hobbies) return []
            return this.user.hobbies.split(', ').filter(h => h)
        },
        hasSocialMedia() {
            return this.user.instagram_username || this.user.tiktok_username ||
                this.user.snapchat_username || this.user.facebook_username
        }
    },
    watch: {
        user: {
            immediate: true,
            handler() {
                this.updateAgeRange()
            }
        }
    },
    methods: {
        uploadPhoto() {
            this.$refs.file.click()
        },
        previewImage(event) {
            const input = event.target
            if (input.files && input.files[0]) {
                this.readImageFile(input.files[0])
            }
        },
        async readImageFile(file) {
            const formData = new FormData()
            formData.append('image', file)
            this.isUploadingPhoto = true
            await UserService.uploadImage(this.user.id, formData)
            this.isUploadingPhoto = false

            const reader = new FileReader()
            reader.onload = (e) => {
                this.$emit('photo-uploaded', e.target.result)
            }
            reader.readAsDataURL(file)
        },
        onHobbiesInput(value) {
            this.$emit('update:user', { ...this.user, hobbies: value.join(', ') })
        },
        updateAgeRange() {
            if (this.user.ideal_roommate_age_young) {
                this.$set(this.ageRange, 0, this.user.ideal_roommate_age_young)
            }
            if (this.user.ideal_roommate_age_old) {
                this.$set(this.ageRange, 1, this.user.ideal_roommate_age_old)
            }
        },
        onAgeRangeChange() {
            this.$emit('update:user', {
                ...this.user,
                ideal_roommate_age_young: this.ageRange[0],
                ideal_roommate_age_old: this.ageRange[1]
            })
        }
    }
}
</script>

<style scoped>
.roommate-profile-section h3 {
    font-size: 26px !important;
    font-weight: 700;
    color: #000;
}

.roommate-profile-section h4 {
    font-size: 20px !important;
    font-weight: 700;
    color: #000;
}

.roommate-profile-section h5 {
    font-size: 14px !important;
    font-weight: 700;
    color: #000;
}

.roommate-profile-section .field-selected {
    background: rgba(45, 118, 227, 0.1);
}

.roommate-profile-section .extra-info {
    font-size: 12px !important;
    color: #000;
    font-style: italic;
}

.roommate-profile-section .hobbies-chip .v-chip__close {
    font-size: 14px !important;
    color: #1976d2;
}

.roommate-profile-section .preference-check label {
    font-size: 14px !important;
    color: #000;
}

.roommate-profile-section .preference-check i {
    color: #d8d8d8;
}

.incomplete-badge {
    font-size: 10px;
    font-weight: 600;
    color: #FF9800;
    background: rgba(255, 152, 0, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
    vertical-align: middle;
}

.roommate-profile-section .v-slider--horizontal .v-slider__track-container {
    height: 4px;
}
</style>
